import { useState } from 'react';
import { DecimalInput } from '../ui/DecimalInput';
import { useQueryClient } from '@tanstack/react-query';
import { Check, RotateCcw, Undo2, X } from 'lucide-react';
import {
  approvalConfig,
  approvalOf,
  approvalText,
  DEFAULT_APPROVAL,
  type ApprovalConfig,
  type ApprovalDecision,
  type BoardSnapshot,
  type CardDetail,
  type CardSummary,
  type CustomField,
} from '@kaba/shared';
import { api, ApiError, newOpId } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { boardKey, cardKey, upsertCards } from '../../lib/board';
import { useReminderTargets } from '../../lib/reminders';
import { toast } from '../../lib/toast';
import { PromptDialog } from '../ui/Prompt';
import { Button, cx } from '../ui/ui';

const STATUS_LOOK = {
  open: 'bg-black/5 text-[var(--ink-soft)] dark:bg-white/10',
  partial: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
  approved: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100',
  rejected: 'bg-rose-100 text-rose-900 dark:bg-rose-900/40 dark:text-rose-100',
} as const;

/**
 * 0.80: Freigabefeld an der Karte – Stand, Freigeben/Ablehnen/Zurückziehen/Zurücksetzen. Der Server
 * entscheidet, wer darf; hier nur Hinweise, die sich aus der Karte ablesen lassen.
 */
export function ApprovalBox({ field, card, snapshot, canEdit }: { field: CustomField; card: CardDetail; snapshot: BoardSnapshot; canEdit: boolean }) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const [reject, setReject] = useState(false);
  const [busy, setBusy] = useState(false);
  const cfg = approvalConfig(field.config);
  const value = card.custom[field.id];
  const v = approvalOf(value);
  const status = v?.status ?? 'open';
  const people = new Map(snapshot.members.map((m) => [m.id, m]));
  const myId = me?.id ?? '';
  const mine = !!v && (v.by.some((b) => b.userId === myId) || v.rejected?.userId === myId);
  const isOwner = snapshot.board.role === 'owner';
  const hint = !canEdit
    ? null
    : cfg.notCreator && card.createdBy === myId
      ? 'Du hast die Karte angelegt – eine andere Person entscheidet.'
      : cfg.notAssignee && card.assigneeIds.includes(myId)
        ? 'Du bist zuständig – eine andere Person entscheidet.'
        : v?.status !== 'rejected' && v?.by.some((b) => b.userId === myId)
          ? v.status === 'partial'
            ? 'Du hast freigegeben – die zweite Freigabe gibt eine andere Person.'
            : null
          : null;
  const mayDecide = canEdit && !hint && status !== 'approved';

  const send = async (decision: ApprovalDecision, reason?: string) => {
    setBusy(true);
    try {
      const res = await api.post<CardSummary>(`/api/cards/${card.id}/approval`, { fieldId: field.id, decision, reason: reason ?? null, opId: newOpId() });
      upsertCards(qc, snapshot.board.id, [res]);
      void qc.invalidateQueries({ queryKey: cardKey(card.id) });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Nicht gespeichert – bitte erneut versuchen.');
      void qc.invalidateQueries({ queryKey: boardKey(snapshot.board.id) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-[var(--line)] p-2.5" role="group" aria-label={field.name}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{field.name}</span>
        <span className={cx('rounded-full px-2 py-0.5 text-xs font-semibold', STATUS_LOOK[status])}>
          {status === 'open' ? 'Offen' : (approvalText(value, people) ?? 'Offen')}
        </span>
      </div>
      {v && <p className="text-sm text-[var(--ink-soft)]">{approvalText(value, people, { long: true })}</p>}
      {cfg.second && status !== 'approved' && (
        <p className="text-xs text-[var(--ink-soft)]">
          Ab {cfg.second.above.toLocaleString('de-DE')} in „{snapshot.fields.find((f) => f.id === cfg.second!.fieldId)?.name ?? 'Feld'}“ geben zwei verschiedene Personen frei.
        </p>
      )}
      {hint && <p className="text-xs text-[var(--ink-soft)]">{hint}</p>}
      {canEdit && (
        <div className="flex flex-wrap gap-2">
          {mayDecide && (
            <>
              <Button size="sm" variant="primary" disabled={busy} onClick={() => void send('approve')}>
                <Check size={15} aria-hidden /> Freigeben
              </Button>
              {status !== 'rejected' && (
                <Button size="sm" disabled={busy} onClick={() => setReject(true)}>
                  <X size={15} aria-hidden /> Ablehnen
                </Button>
              )}
            </>
          )}
          {mine && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void send('withdraw')}>
              <Undo2 size={15} aria-hidden /> Zurückziehen
            </Button>
          )}
          {isOwner && v && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void send('reset')}>
              <RotateCcw size={15} aria-hidden /> Zurücksetzen
            </Button>
          )}
        </div>
      )}
      <PromptDialog
        open={reject}
        title={`„${field.name}“ ablehnen`}
        label="Begründung"
        confirm="Ablehnen"
        hint="Die Begründung steht an der Karte und im Verlauf."
        onClose={() => setReject(false)}
        onSubmit={(reason) => {
          if (reason.trim().length < 3) return toast.error('Bitte kurz begründen.');
          setReject(false);
          void send('reject', reason.trim());
        }}
      />
    </div>
  );
}

const sel = 'min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 text-base sm:min-h-10 sm:text-sm';

/** 0.80: Einstellungen eines Freigabefelds im Feld-Dialog (nur Besitzer*innen) */
export function ApprovalSettings({ snapshot, selfId, value, onChange }: { snapshot: BoardSnapshot; selfId: string | null; value: ApprovalConfig | null; onChange: (c: ApprovalConfig) => void }) {
  const c = value ?? DEFAULT_APPROVAL;
  const { data: targets } = useReminderTargets(snapshot.board.id, true);
  const groups = targets?.groups ?? [];
  const numberFields = snapshot.fields.filter((f) => f.type === 'number' || f.type === 'currency');
  const others = snapshot.fields.filter((f) => f.id !== selfId && f.type !== 'approval');
  const set = (p: Partial<ApprovalConfig>) => onChange({ ...c, ...p });
  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const restricted = c.approvers.users.length > 0 || c.approvers.groups.length > 0;
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-[var(--line)] p-3">
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-sm font-medium">Wer darf freigeben?</legend>
        <label className="flex min-h-10 items-center gap-2 text-sm">
          <input type="radio" name="appr-who" className="size-5 accent-[var(--color-accent)]" checked={!restricted} onChange={() => set({ approvers: { users: [], groups: [] } })} />
          Alle mit Bearbeitungsrecht
        </label>
        <label className="flex min-h-10 items-center gap-2 text-sm">
          <input
            type="radio"
            name="appr-who"
            className="size-5 accent-[var(--color-accent)]"
            checked={restricted}
            onChange={() => set({ approvers: { users: groups.length ? [] : snapshot.members.slice(0, 1).map((m) => m.id), groups: groups.slice(0, 1).map((g) => g.id) } })}
          />
          Nur bestimmte Personen oder Gruppen
        </label>
        {restricted && (
          <div className="flex flex-wrap gap-1.5 pl-7">
            {groups.map((g) => (
              <button
                key={g.id}
                type="button"
                aria-pressed={c.approvers.groups.includes(g.id)}
                onClick={() => set({ approvers: { ...c.approvers, groups: toggle(c.approvers.groups, g.id) } })}
                className={cx('min-h-9 rounded-lg border px-2.5 text-sm', c.approvers.groups.includes(g.id) ? 'border-accent bg-accent-soft font-semibold' : 'border-[var(--line)]')}
              >
                Gruppe {g.name}
              </button>
            ))}
            {snapshot.members.map((m) => (
              <button
                key={m.id}
                type="button"
                aria-pressed={c.approvers.users.includes(m.id)}
                onClick={() => set({ approvers: { ...c.approvers, users: toggle(c.approvers.users, m.id) } })}
                className={cx('min-h-9 rounded-lg border px-2.5 text-sm', c.approvers.users.includes(m.id) ? 'border-accent bg-accent-soft font-semibold' : 'border-[var(--line)]')}
              >
                {m.name}
              </button>
            ))}
          </div>
        )}
      </fieldset>
      <label className="flex min-h-10 items-center gap-2 text-sm">
        <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={c.notCreator} onChange={(e) => set({ notCreator: e.target.checked })} />
        Nicht die Person, die die Karte angelegt hat
      </label>
      <label className="flex min-h-10 items-center gap-2 text-sm">
        <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={c.notAssignee} onChange={(e) => set({ notAssignee: e.target.checked })} />
        Nicht die Zuständigen
      </label>
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-sm font-medium">Zweite Freigabe</legend>
        {numberFields.length === 0 ? (
          <p className="text-xs text-[var(--ink-soft)]">Lege ein Zahl- oder Betragsfeld an, um ab einem Wert zwei Freigaben zu verlangen.</p>
        ) : (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <select
              aria-label="Zweite Freigabe ab Feld"
              className={sel}
              value={c.second?.fieldId ?? ''}
              onChange={(e) => set({ second: e.target.value ? { fieldId: e.target.value, above: c.second?.above ?? 1000 } : null })}
            >
              <option value="">nicht nötig</option>
              {numberFields.map((f) => (
                <option key={f.id} value={f.id}>
                  wenn „{f.name}“
                </option>
              ))}
            </select>
            {c.second && (
              <>
                <span>größer als</span>
                <DecimalInput
                  aria-label="Grenze für die zweite Freigabe"
                  min={0}
                  allowEmpty={false}
                  className={cx(sel, 'w-28')}
                  value={c.second.above}
                  onChange={(n) => n !== null && set({ second: { ...c.second!, above: n } })}
                />
              </>
            )}
          </div>
        )}
      </fieldset>
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-sm font-medium">Freigabe verfällt, wenn sich danach etwas ändert</legend>
        <label className="flex min-h-10 items-center gap-2 text-sm">
          <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={c.resetFiles} onChange={(e) => set({ resetFiles: e.target.checked })} />
          Dateien an der Karte (z. B. ein anderer Beleg)
        </label>
        {others.map((f) => (
          <label key={f.id} className="flex min-h-10 items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-5 accent-[var(--color-accent)]"
              checked={c.resetFields.includes(f.id) || c.second?.fieldId === f.id}
              disabled={c.second?.fieldId === f.id}
              onChange={() => set({ resetFields: toggle(c.resetFields, f.id) })}
            />
            „{f.name}“{c.second?.fieldId === f.id ? ' (immer – entscheidet über die zweite Freigabe)' : ''}
          </label>
        ))}
      </fieldset>
    </div>
  );
}
